import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const Switch = ({ 
  label = "", 
  checked = false, 
  onChange,
  disabled = false,
  labelPosition = "right",
  size = "default",
  color = "blue" // Add color prop
}) => {
  const [isChecked, setIsChecked] = useState(checked);
  
  // Update internal state when checked prop changes
  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  const handleChange = () => {
    if (disabled) return;
    const newValue = !isChecked;
    setIsChecked(newValue);
    if (onChange) onChange(newValue);
  };

  // Color variants
  const colorClasses = {
    blue: "peer-checked:bg-blue-600 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800",
    green: "peer-checked:bg-green-500 peer-focus:ring-green-300 dark:peer-focus:ring-green-800",
    red: "peer-checked:bg-red-500 peer-focus:ring-red-300 dark:peer-focus:ring-red-800",
    purple: "peer-checked:bg-purple-600 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800",
    indigo: "peer-checked:bg-indigo-600 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800",
  };

  // Size variations with improved proportions
  const sizeClasses = {
    small: {
      container: "w-8 h-4",
      circle: "after:h-3 after:w-3 after:top-[2px] after:start-[2px]"
    },
    default: {
      container: "w-11 h-6",
      circle: "after:h-5 after:w-5 after:top-[2px] after:start-[2px]"
    },
    large: {
      container: "w-14 h-7",
      circle: "after:h-6 after:w-6 after:top-[2px] after:start-[2px]"
    }
  };

  const containerSize = sizeClasses[size]?.container || sizeClasses.default.container;
  const circleSize = sizeClasses[size]?.circle || sizeClasses.default.circle;
  const selectedColor = colorClasses[color] || colorClasses.blue;
  
  return (
    <label 
      className={`inline-flex items-center ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      onClick={(e) => !disabled && e.stopPropagation()}
    >
      {labelPosition === "left" && label && (
        <span className="mr-3 text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      )}
      
      <div className="relative">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={isChecked}
          onChange={handleChange}
          disabled={disabled}
        />
        <div
          className={`
            ${containerSize} 
            ${circleSize}
            ${selectedColor}
            bg-gray-200 
            dark:bg-gray-700
            peer-focus:outline-none 
            peer-focus:ring-4 
            rounded-full 
            peer
            relative
            after:content-[''] 
            after:absolute 
            after:bg-white
            after:border-gray-300 
            after:border 
            after:rounded-full 
            after:transition-all 
            peer-checked:after:translate-x-full 
            rtl:peer-checked:after:-translate-x-full 
            peer-checked:after:border-white
            shadow-inner
          `}
        />
      </div>
      
      {labelPosition === "right" && label && (
        <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      )}
    </label>
  );
};

Switch.propTypes = {
  label: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  labelPosition: PropTypes.oneOf(['left', 'right']),
  size: PropTypes.oneOf(['small', 'default', 'large']),
  color: PropTypes.oneOf(['blue', 'green', 'red', 'purple', 'indigo'])
};

export default Switch;